import { useMemo } from 'react';

import {
  OrderDispositionFilterOptions,
  PurchaseTargetLineStatus,
  RecurrencyReplenishmentMethod,
  TransferTargetReviewMethod,
} from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { PurchaseTargetLineDTO } from '@recurrency/core-api-schema/dist/purchasing/getPurchaseTargetLines';

import { MultiSelectOption } from 'components/AsyncSelect/types';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { arrUnique } from 'utils/array';
import { truthy } from 'utils/boolean';
import { formatIdNameObj, pluralize } from 'utils/formatting';
import { useHashState } from 'utils/routes';
import { isSpecialOrderPurchasingEnabled, showSpecialOrderWarning } from 'utils/tenantSettings';

import { PurchaseTargetLinesHashState, PurchasingLineTransfer } from 'types/hash-state';

import {
  GenericTargetLineDTO,
  GenericTargetLineRow,
  getIsGroupBuy,
  PurchaseTargetLineRow,
  PurchasingType,
  PurchasingTypeConfig,
} from '../types';

export const allStatuses = Object.values(PurchaseTargetLineStatus);
export const nonStockStatuses = allStatuses.filter((s) => s !== PurchaseTargetLineStatus.Nonstock);

export function focusedRowClassName(record: GenericTargetLineRow, focusedLine?: GenericTargetLineRow) {
  return [
    'focusable-row',
    record.key === focusedLine?.key ? 'focused-row' : '',
    record.userQtyToOrder + additionalPurchasingQty(record) > 0 ? 'purchasing-row' : '',
  ]
    .filter(truthy)
    .join(' ');
}

export function getUniqueItemGroupOptions(targetLines: GenericTargetLineDTO[]) {
  const options: MultiSelectOption[] = arrUnique(
    targetLines.filter((tl) => !!tl.itemGroupId),
    'itemGroupId',
  ).map((tl) => ({
    value: tl.itemGroupId!,
    label: formatIdNameObj({ foreignId: tl.itemGroupId!, name: tl.itemGroupName ?? '' }),
  }));

  return options;
}

export function getTransferKey(record: PurchaseTargetLineDTO): string {
  return `${record.supplierId}|${record.locationId}|${record.itemId}`;
}

export const daysOfWeekDef = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

export function reviewScheduleToReadable(
  cycleDays: number | undefined,
  scheduleDaysOfWeek: string,
  scheduleWeekCadence: number,
  method: TransferTargetReviewMethod,
  detailed?: boolean,
) {
  const daysOfWeekAbr = daysOfWeekDef.map((day) => day.label);
  if (method === TransferTargetReviewMethod.Cycle) {
    return `Every ${cycleDays ?? '-'} days`;
  }
  const daysOfWeek = scheduleDaysOfWeek.split(',');
  if (detailed) {
    const daysOfWeekNames = daysOfWeek.map((day) => daysOfWeekAbr[parseInt(day, 10)]).join(', ');
    return `${daysOfWeekNames} every ${pluralize(scheduleWeekCadence, 'week', 'weeks', scheduleWeekCadence !== 1)}`;
  }
  if (scheduleWeekCadence === 1) {
    return `${pluralize(daysOfWeek.length, 'day', 'days', true)} per week`;
  }
  return `Every ${scheduleWeekCadence} weeks`;
}

export const orderDispositionLabels: MultiSelectOption[] = [
  { label: 'Special Order', value: OrderDispositionFilterOptions.SpecialOrder },
  { label: 'Backorder', value: OrderDispositionFilterOptions.BackOrder },
];

export const usePurchasingStatuses = () => {
  const { activeTenant, activeUser, activeTenantSettings } = useGlobalApp();
  const [hashState] = useHashState<PurchaseTargetLinesHashState>();
  const isGroupBuy = hashState?.groupBuy;
  const shouldShowSpecialOrderWarning = useMemo(
    () => !isGroupBuy && showSpecialOrderWarning(activeTenant),
    [activeTenant, isGroupBuy],
  );
  const shouldCreateSpecialOrderLines = useMemo(
    () => !isGroupBuy && isSpecialOrderPurchasingEnabled(activeTenant, activeUser),
    [activeTenant, activeUser, isGroupBuy],
  );

  const filteredStatuses = useMemo(
    () => allStatuses.filter((s) => s !== PurchaseTargetLineStatus.Special || shouldCreateSpecialOrderLines),
    [shouldCreateSpecialOrderLines],
  );

  const defaultStatusFilters = filteredStatuses.filter((s) => s !== PurchaseTargetLineStatus.Nonstock);

  const enabledPriorityDisposition = orderDispositionLabels.filter(
    (x) => x.value !== OrderDispositionFilterOptions.SpecialOrder || shouldShowSpecialOrderWarning,
  );

  const automaticallyRecommendPurchasingSpecials =
    !!activeTenantSettings?.[TenantSettingKey.FeaturePurchasingSpecialOrderDefaultRecommendation] &&
    shouldShowSpecialOrderWarning;

  return {
    filteredStatuses,
    defaultStatusFilters,
    shouldCreateSpecialOrderLines,
    shouldShowSpecialOrderWarning,
    enabledPriorityDisposition,
    automaticallyRecommendPurchasingSpecials,
  };
};

export const PurchasingTypes: Record<PurchasingType, PurchasingTypeConfig> = {
  [PurchasingType.SpecialOrder]: {
    condition: () => isSpecialOrderPurchasingEnabled() && !getIsGroupBuy(),
    qtyField: 'userQtyForSpecialOrders',
  },
};

/**
 * Purpose of this is to calculate the values for additional purchasing fields, ex: special orders, dropships
 * with respect to settings. Modify PurchasingTypes array above to add a new quantity field.
 *
 * @param line to get purchasing values from
 * @param purchasingTypeFilter optional array of purchasing types to filter by, if null returns everything
 * @returns Sum of purchasing fields that are not qtyToOrder
 */
export function additionalPurchasingQty(line: GenericTargetLineRow, purchasingTypeFilter: PurchasingType[] = []) {
  let validTypes = purchasingTypeFilter.length
    ? purchasingTypeFilter.map((type) => PurchasingTypes[type])
    : Object.values(PurchasingTypes);
  validTypes = validTypes.filter((type) => type.condition());
  return validTypes.reduce((acc, curr) => acc + (Number(line[curr.qtyField]) || 0), 0);
}

export function getTotalUserTransfersQty(transfers?: PurchasingLineTransfer[]): number {
  return (transfers || []).reduce((total, transfer) => total + transfer.qtyToTransfer, 0);
}

/**
 * Calculate the quantity of this item being transferred out of this location in draft transfers that have not yet been saved
 * @param record record being rendered (row within the table of the modal)
 * @param targetLine target line being edited (primary modal line)
 * @param allLines all in progress target lines
 * @returns quantity of this item being transferred out of this location in draft transfers
 */
export const getQtyOnDraftTransfers = (
  record: PurchaseTargetLineDTO,
  targetLine: PurchaseTargetLineRow,
  allLines: GenericTargetLineRow[],
) =>
  allLines.reduce((total, line) => {
    if (line.itemId !== record.itemId || line.locationId === targetLine.locationId || !line.userTransfers?.length) {
      return total;
    }
    const transferAmount =
      line.userTransfers.find((transfer) => transfer.transferFromLocationId === record.locationId)?.qtyToTransfer || 0;
    return total + transferAmount;
  }, 0);

export const calculateOverUnderBy = (
  replenishmentMethod: RecurrencyReplenishmentMethod,
  netStock: number,
  invMax: number,
  invMin: number,
) => {
  if (replenishmentMethod === RecurrencyReplenishmentMethod.OPOQ) {
    if (netStock > invMax + invMin) {
      return netStock - invMax - invMin;
    }
    if (netStock < invMin) {
      return netStock - invMin;
    }
    return 0;
  }
  if (netStock > invMax) {
    return netStock - invMax;
  }
  if (netStock < invMin) {
    return netStock - invMin;
  }
  return 0;
};

export const filterTargetLinesByPgMinId = (
  targetLines: GenericTargetLineDTO[] = [],
  pgMinId?: string,
): GenericTargetLineDTO[] =>
  pgMinId ? targetLines.filter((line) => line.itemGroupId && line.itemGroupId === pgMinId) : targetLines;
